import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { PencilSquareIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { getVenuesByOrgID } from '../../util/util';
import VenueEditModal from './modals/VenueEditModal';
import VenueArchiveModal from './modals/VenueArchiveModal';
import IconActionButton from './ui/IconActionButton';
import VenueCard from './VenueCard';
import ToggleButton from './ui/ToggleButton';

const byClosedLast = (a, b) => (a.is_closed === b.is_closed ? 0 : a.is_closed ? 1 : -1);

const VenueSection = forwardRef(function VenueSection({
  orgId,
  selectedVenueId,
  onSelectVenue,
  onShowsNeedRefresh,
  autoScroll = false,
  onAutoScrolled,
  isSuperadmin = false,
}, ref) {
  const [venues, setVenues] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [isVenueActionsModalOpen, setIsVenueActionsModalOpen] = useState(false);
  const [showArchivedVenues, setShowArchivedVenues] = useState(false);
  const [venueModalKey, setVenueModalKey] = useState(0);

  const venuesSectionRef = useRef(null);
  const archivedVenuesSectionRef = useRef(null);
  const hasScrolledToVenuesRef = useRef(false);

  const openEditModal = venue => {
    setSelectedVenue(venue);
    setVenueModalKey(k => k + 1);
    setIsVenueModalOpen(true);
  };

  const openArchiveModal = venue => {
    setSelectedVenue(venue);
    setIsVenueActionsModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    getActiveVenues: () => (venues ? venues.filter(v => !v.is_archived).sort(byClosedLast) : null),
    openEditModalForVenue: openEditModal,
    closeVenueModal: () => setIsVenueModalOpen(false),
  }), [venues]);

  // Fetch venues whenever the org changes
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    hasScrolledToVenuesRef.current = false;
    (async () => {
      try {
        const data = await getVenuesByOrgID(orgId);
        if (!cancelled && data) setVenues(data);
      } catch (error) {
        console.error('Error fetching venues:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Scroll to venues section once per org load, only when org was selected interactively
  useEffect(() => {
    if (autoScroll && venues && !hasScrolledToVenuesRef.current && venuesSectionRef.current) {
      venuesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      hasScrolledToVenuesRef.current = true;
      onAutoScrolled?.();
    }
  }, [autoScroll, venues, onAutoScrolled]);

  const handleVenueSaved = (venue, isNew) => {
    if (isNew) {
      setVenues(prev => [...(prev ?? []), venue]);
      onSelectVenue?.(venue.venue_id);
    } else {
      setVenues(prev =>
        prev.map(v => (v.venue_id === venue.venue_id ? { ...v, ...venue } : v))
      );
    }
  };

  const handleVenueArchived = (venueId, newArchivedValue) => {
    setVenues(prev =>
      prev.map(v => (v.venue_id === venueId ? { ...v, is_archived: newArchivedValue } : v))
    );
    if (newArchivedValue) {
      if (selectedVenueId === venueId) {
        onSelectVenue?.(null);
      }
    } else {
      onSelectVenue?.(venueId);
    }
  };

  const handleVenuesCombined = (keepVenueId, archivedVenueId) => {
    setVenues(prev =>
      prev.map(v => (v.venue_id === archivedVenueId ? { ...v, is_archived: true } : v))
    );
    if (selectedVenueId === keepVenueId) {
      onShowsNeedRefresh?.();
    } else {
      onSelectVenue?.(keepVenueId);
    }
  };

  if (!venues) {
    return (
      <div className="mx-8 mb-8">
        <div className="flex items-center justify-between pb-6">
          <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-8 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map(i => (
            <div key={i} className="card w-full border border-slate-200">
              <div className="px-6 py-4 space-y-2">
                <div className="h-5 bg-slate-200 rounded w-3/4 animate-pulse" />
                <div className="h-4 bg-slate-100 rounded w-1/2 animate-pulse" />
                <div className="h-4 bg-slate-100 rounded w-1/3 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeVenues = venues.filter(v => !v.is_archived).sort(byClosedLast);
  const archivedVenues = venues.filter(v => v.is_archived).sort(byClosedLast);
  const combineTargets =
    selectedVenue && !selectedVenue.is_archived
      ? activeVenues.filter(v => v.venue_id !== selectedVenue.venue_id)
      : [];

  return (
    <>
      <div className="mx-8 mb-8" ref={venuesSectionRef}>
        <div className="flex items-center justify-between pb-6">
          <h2 className="text-2xl text-secondary-red font-semibold tracking-wide uppercase">Venues</h2>
          <div className="flex gap-2">
            <button
              data-tour="add-venue-button"
              className="btn btn-outline btn-sm focus:outline-none"
              onClick={() => openEditModal(null)}
            >
              + Add Venue
            </button>
            {isSuperadmin && (
              <ToggleButton
                isJoinItem={false}
                size="sm"
                active={showArchivedVenues}
                aria-pressed={showArchivedVenues}
                onClick={() => {
                  const next = !showArchivedVenues;
                  setShowArchivedVenues(next);
                  if (next) {
                    setTimeout(() => {
                      archivedVenuesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 0);
                  }
                }}
              >
                Display Archived
              </ToggleButton>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeVenues.map((venue, index) => (
            <VenueCard
              key={venue.venue_id}
              venue={venue}
              data-tour={index === 0 ? 'venue-card-0' : undefined}
              className={`cursor-pointer border transition-colors ${
                selectedVenueId === venue.venue_id
                  ? 'bg-secondary-red text-white border-transparent'
                  : 'bg-white text-black border-secondary-red hover:bg-secondary-red hover:text-white hover:border-transparent'
              }`}
              cityClassName="opacity-60"
              onClick={() => onSelectVenue?.(venue.venue_id)}
              actions={
                <>
                  <IconActionButton
                    label="Edit venue"
                    icon={PencilSquareIcon}
                    iconClassName=""
                    data-tour={index === 0 ? 'venue-edit-button-0' : undefined}
                    onClick={e => {
                      e.stopPropagation();
                      openEditModal(venue);
                    }}
                  />
                  {isSuperadmin && (
                    <IconActionButton
                      label="Archive venue"
                      icon={ArchiveBoxIcon}
                      iconClassName=""
                      data-tour={index === 0 ? 'venue-archive-button-0' : undefined}
                      onClick={e => {
                        e.stopPropagation();
                        openArchiveModal(venue);
                      }}
                    />
                  )}
                </>
              }
            />
          ))}
        </div>

        {isSuperadmin && showArchivedVenues && (
          <div className="mt-8 px-6" ref={archivedVenuesSectionRef}>
            <h3 className="text-lg font-semibold text-secondary-red pb-4">Archived Venues</h3>
            {archivedVenues.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {archivedVenues.map(venue => (
                  <VenueCard
                    key={venue.venue_id}
                    venue={venue}
                    className="bg-white text-black border border-secondary-red opacity-60"
                    actions={
                      <>
                        <IconActionButton
                          label="Edit venue"
                          icon={PencilSquareIcon}
                          iconClassName=""
                          onClick={() => openEditModal(venue)}
                        />
                        <IconActionButton
                          label="Restore venue"
                          icon={ArchiveBoxIcon}
                          iconClassName=""
                          onClick={() => openArchiveModal(venue)}
                        />
                      </>
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">No archived venues.</p>
            )}
          </div>
        )}
      </div>

      <VenueEditModal
        key={venueModalKey}
        isModalOpen={isVenueModalOpen}
        setIsModalOpen={setIsVenueModalOpen}
        selectedItem={selectedVenue}
        orgId={orgId}
        onVenueSaved={handleVenueSaved}
      />
      <VenueArchiveModal
        isModalOpen={isVenueActionsModalOpen}
        setIsModalOpen={setIsVenueActionsModalOpen}
        selectedItem={selectedVenue}
        combineTargets={combineTargets}
        onVenueArchived={handleVenueArchived}
        onVenuesCombined={handleVenuesCombined}
      />
    </>
  );
});

export default VenueSection;
